import { useEffect, useMemo, useState } from 'react';
import {
  CONSENT_STORAGE_KEY,
  defaultCookiePreferences,
  readStoredConsent,
  buildConsentPayload,
} from '../lib/cookieConsent';

function persistConsent(preferences) {
  if (typeof window === 'undefined') return;
  const payload = buildConsentPayload(preferences);
  window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(payload));
  document.cookie = `vn_cookie_consent=accepted; Max-Age=${60 * 60 * 24 * 365}; Path=/; SameSite=Lax`;
  window.dispatchEvent(
    new CustomEvent('vn-cookie-consent-updated', {
      detail: payload,
    })
  );
}

export default function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [hasStoredConsent, setHasStoredConsent] = useState(false);
  const [preferences, setPreferences] = useState(defaultCookiePreferences);

  useEffect(() => {
    const stored = readStoredConsent();
    if (!stored) {
      setVisible(true);
      setHasStoredConsent(false);
      return;
    }

    setPreferences(stored.preferences || defaultCookiePreferences);
    setHasStoredConsent(true);
  }, []);

  const summaryText = useMemo(() => {
    if (preferences.analytics && preferences.marketing) return 'Cookies de desempenho e marketing ativados.';
    if (preferences.analytics) return 'Cookies de desempenho ativados.';
    return 'Somente cookies essenciais ativos.';
  }, [preferences.analytics, preferences.marketing]);

  const closeManager = () => {
    if (!hasStoredConsent) return;
    setVisible(false);
    setShowPreferences(false);
  };


  const handleAcceptAll = () => {
    const next = { necessary: true, analytics: true, marketing: true };
    setPreferences(next);
    persistConsent(next);
    setHasStoredConsent(true);
    setVisible(false);
    setShowPreferences(false);
  };

  const handleEssentialOnly = () => {
    const next = { necessary: true, analytics: false, marketing: false };
    setPreferences(next);
    persistConsent(next);
    setHasStoredConsent(true);
    setVisible(false);
    setShowPreferences(false);
  };

  const handleSavePreferences = () => {
    persistConsent(preferences);
    setHasStoredConsent(true);
    setVisible(false);
    setShowPreferences(false);
  };

  if (!visible && !hasStoredConsent) return null;

  return (
    <>

      {visible && (
        <div className="fixed inset-x-0 bottom-0 z-[120] px-4 pb-4 md:px-6">
          <div className="mx-auto w-full max-w-4xl rounded-2xl border border-[#EADCD8] bg-white/95 p-4 shadow-2xl backdrop-blur-md md:p-5">
            <div className="flex flex-col gap-3">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.16em] text-[#7E6B5F]">Privacidade e cookies</p>
                <h3 className="mt-1 text-lg font-semibold text-[#1C1C1C]" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Usamos cookies para melhorar sua experiência
                </h3>
                <p className="mt-1 text-sm text-[#333333]">
                  Utilizamos cookies essenciais para o funcionamento do site e, com seu consentimento, cookies opcionais
                  para métricas e personalização. Você pode alterar suas escolhas quando quiser.
                </p>
                <p className="mt-1 text-xs text-[#7E6B5F]">{summaryText}</p>
              </div>

              {showPreferences && (
                <div className="grid gap-2 rounded-xl border border-[#EADCD8] bg-[#FCFAF9] p-3 text-sm">
                  <label className="flex items-center justify-between gap-3">
                    <span className="text-[#1C1C1C]">Cookies essenciais (obrigatórios)</span>
                    <span className="rounded-full bg-[#E8B7B1]/20 px-2 py-0.5 text-xs font-semibold text-[#7E6B5F]">Sempre ativo</span>
                  </label>
                  <label className="flex items-center justify-between gap-3">
                    <span className="text-[#1C1C1C]">Cookies de desempenho (analytics)</span>
                    <input
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={(event) => setPreferences((current) => ({ ...current, analytics: event.target.checked }))}
                      className="h-4 w-4 accent-[#E8B7B1]"
                    />
                  </label>
                  <label className="flex items-center justify-between gap-3">
                    <span className="text-[#1C1C1C]">Cookies de marketing</span>
                    <input
                      type="checkbox"
                      checked={preferences.marketing}
                      onChange={(event) => setPreferences((current) => ({ ...current, marketing: event.target.checked }))}
                      className="h-4 w-4 accent-[#E8B7B1]"
                    />
                  </label>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowPreferences((current) => !current)}
                  className="rounded-full border border-[#EADCD8] px-4 py-2 text-sm font-semibold text-[#7E6B5F] transition hover:bg-[#F8F1EF]"
                >
                  {showPreferences ? 'Fechar preferências' : 'Personalizar'}
                </button>
                <button
                  type="button"
                  onClick={handleEssentialOnly}
                  className="rounded-full border border-[#EADCD8] px-4 py-2 text-sm font-semibold text-[#7E6B5F] transition hover:bg-[#F8F1EF]"
                >
                  Somente essenciais
                </button>
                <button
                  type="button"
                  onClick={handleAcceptAll}
                  className="rounded-full bg-[#E8B7B1] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#DFA8A1]"
                >
                  Aceitar todos
                </button>
                {showPreferences && (
                  <button
                    type="button"
                    onClick={handleSavePreferences}
                    className="rounded-full border border-[#E8B7B1] px-4 py-2 text-sm font-semibold text-[#1C1C1C] transition hover:bg-[#FCEFED]"
                  >
                    Salvar preferências
                  </button>
                )}
                {hasStoredConsent && (
                  <button
                    type="button"
                    onClick={closeManager}
                    className="rounded-full px-4 py-2 text-sm font-semibold text-[#7E6B5F] transition hover:bg-[#F8F1EF]"
                  >
                    Fechar
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
